import { Chip, Container, Spacer } from '@cleartrip/ct-design-components'
import React, { useState } from 'react'

import { Raven } from '@/analytics/raven'
import { RAVEN_ATTRIBUTE_NAME, RAVEN_EVENT_NAME, RAVEN_PAGE_NAME } from '@/analytics/raven/ravenConstants'
import { ravenSDKTrigger } from '@/analytics/raven/ravenSDKHelper'
import { PersonFilled, SelectedPerson, UpChevron } from '@/assets/icons'
import { Dropdown, DropdownMenu, DropdownSelect } from '@/components/oldComponents/Dropdown'
import { queryFormat } from '@/components/oldComponents/Dropdown/HomeSearchContainer'
import usePremiumEconomyExperiment from '@/hooks/flights/usePremiumEconomyExperiment'

import Travellers from './Traveller/Traveller'

interface TravellerAndClassDropdownProps {
    query: queryFormat
    activeFare: string
    setQuery: (newQuery: Partial<queryFormat>) => void
    /**
     * Currently picked route. `query.from`/`query.to` are only filled on search submit,
     * so the sector gate has to be fed the form's live selection instead.
     */
    isIntl?: boolean
}

const ECONOMY_PE_COMBINED_LABEL = 'Economy/Premium Economy'

const TravellerAndClassDropdown: React.FC<TravellerAndClassDropdownProps> = ({ query, setQuery, activeFare, isIntl }) => {
    const [openMenu, setOpenMenu] = useState(false)
    // The home page has no search to resolve a sector from, so the gate takes the route
    // straight off the form — the pill must react as the user picks it.
    const { isPeTestVariant } = usePremiumEconomyExperiment(isIntl)
    // Combined pill is test-only; control keeps plain "Economy".
    const economyLabel = isPeTestVariant ? ECONOMY_PE_COMBINED_LABEL : 'Economy'

    const selectedClassName: Record<any, any> = {
        Economy: economyLabel,
        ['Premium Economy']: 'Premium economy',
        Business: 'Business class',
        First: 'First class'
    }
    const selectedClassNameRecord: Record<string, string> = {
        Economy: 'Economy',
        [ECONOMY_PE_COMBINED_LABEL]: 'Economy',
        ['Business class']: 'Business',
        ['First class']: 'First',
        ['Premium economy']: 'Premium Economy'
    }

    function setClass(selectedclass: string) {
        Raven.push({
            eventName: RAVEN_EVENT_NAME.HOME_TRAVEL_CLASS_UPDATED,
            eventData: {
                action_name: RAVEN_ATTRIBUTE_NAME.HP_TRAVEL_CLASS_UPDATED,
                page_name: RAVEN_PAGE_NAME.FLIGHTS_HOME
            }
        })
        setQuery({
            ...query,
            class: selectedClassNameRecord[selectedclass]
        })
    }

    const tripTypeDropdown = (
        <Container className="w-100p px-3 pt-3 pb-6">
            <Travellers activeFare={activeFare} query={query} setQuery={setQuery} />
            <Spacer className="pt-4" />
            <Container className="flex flex-middle flex-wrap" style={{ gap: '12px' }}>
                {Object.values(selectedClassName).map((item, index) => {
                    return (
                        <Chip
                            key={index}
                            isSelected={selectedClassName[query.class] === item}
                            label={item}
                            styleConfig={{
                                labelTypography: {
                                    className: 'c-pointer fw-400'
                                },
                                root: {
                                    className: 'c-pointer'
                                }
                            }}
                            onClick={() => {
                                setClass(item)
                            }}
                        />
                    )
                })}
            </Container>
        </Container>
    )
    const handleDropdownToggle = () => {
        setOpenMenu(!openMenu)
    }
    const getLabel = (open?: boolean) => {
        return (
            <>
                <Container className="flex flex-middle">
                    {open ? <SelectedPerson /> : <PersonFilled />}
                    <span className="fw-500 fs-16 ml-2" style={{ lineHeight: '24px', maxWidth: '270px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {`${parseInt(query.adults)} ${parseInt(query.adults) > 1 ? 'Adults' : 'Adult'}${
                            parseInt(query.childs)
                                ? `, ${query.childs} ${parseInt(query.childs) > 1 ? 'Children' : 'Child'}`
                                : ''
                        }${
                            parseInt(query.infants)
                                ? `, ${query.infants} ${parseInt(query.infants) > 1 ? 'Infants' : 'Infant'}`
                                : ''
                        }, ${selectedClassName[query.class]}`}
                    </span>
                    <span
                        className="ml-1 flex"
                        style={{ rotate: open ? '0deg' : '180deg', transition: 'ease .5s rotate' }}
                    >
                        <UpChevron />
                    </span>
                </Container>
            </>
        )
    }

    const travellerInputClick = () => {
        ravenSDKTrigger('a_hp_pax_travel_class_selection_clicked', {
            action_name: 'a_pax_travel_class_selection_click',
            page_name: 'flights_home'
        })
        setOpenMenu(!openMenu)
    }

    return (
        <Container className="homePageTraveller">
            <Dropdown
                onOutsideClick={() => {
                    setOpenMenu(false)
                }}
            >
                <DropdownSelect
                    border={false}
                    className="fs-2"
                    data-testid='paxClassContainerId'
                    getLabel={(open: boolean) => getLabel(open)}
                    name="travellers"
                    open={openMenu}
                    placeHolder="Select traveller"
                    size={'sm'}
                    tabIndex={4}
                    toggleDropdown={handleDropdownToggle}
                    width={220}
                    onClick={travellerInputClick}
                />
                <DropdownMenu className="dropDown_menu_widthImp" distance={-12} open={openMenu}>
                    {tripTypeDropdown}
                </DropdownMenu>
            </Dropdown>
        </Container>
    )
}

export default TravellerAndClassDropdown
